import { Cpu } from 'lucide-react';
import {
  Button,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import type { ConfigFlavor } from '@/api/opennebula';
import { SectionCard, FlavorGrid, FLAVOR_TYPES, type CreateState } from './shared';

interface GpuModel {
  id: string;
  label?: string;
  vram?: number;
}

interface ResourceSectionProps {
  form: CreateState;
  patch: (p: Partial<CreateState>) => void;
  visibleFlavors: ConfigFlavor[];
  gpuModels: GpuModel[];
}

/** Instance Resource Configuration — flavor type tabs + flavor grid + GPU. */
export function ResourceConfigCard({ form, patch, visibleFlavors, gpuModels }: ResourceSectionProps) {
  return (
    <SectionCard
      title="Instance Resource Configuration"
      description="Pick the instance type that's right for you, whether you need more CPU power or more RAM."
    >
      {!form.gpuEnabled && (
        <SegmentedControl
          items={FLAVOR_TYPES}
          value={form.flavorType}
          onValueChange={(v) => patch({ flavorType: v as CreateState['flavorType'], flavor: '' })}
          aria-label="Flavor type"
        />
      )}
      <div style={{ marginTop: 'var(--vn-space-3)' }}>
        <FlavorGrid flavors={visibleFlavors} value={form.flavor} onPick={(name) => patch({ flavor: name })} />
      </div>
      {gpuModels.length > 0 && (
        <div style={{ marginTop: 'var(--vn-space-4)', display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <Button
            type="button"
            size="sm"
            variant={form.gpuEnabled ? 'primary' : 'secondary'}
            startIcon={<Cpu size={15} />}
            onClick={() =>
              patch({
                gpuEnabled: !form.gpuEnabled,
                flavor: '',
                gpuModel: form.gpuEnabled ? '' : gpuModels[0]?.id ?? '',
              })
            }
          >
            {form.gpuEnabled ? 'GPU acceleration enabled' : 'Enable GPU acceleration'}
          </Button>
          {form.gpuEnabled && (
            <Select value={form.gpuModel} onValueChange={(v) => patch({ gpuModel: v })}>
              <SelectTrigger style={{ width: 'min(220px, 100%)' }}>
                <SelectValue placeholder="GPU model" />
              </SelectTrigger>
              <SelectContent>
                {gpuModels.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.label || g.id}
                    {g.vram ? ` · ${g.vram} GB` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </SectionCard>
  );
}
